import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Bus, Check, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getGuardTrustedOperators, recordTrustedOperatorEntry } from '@/services/api.client';
import type { TrustedOperator, TrustedOperatorEntryResult } from '@/services/api.client';

type Step = 'select' | 'confirm' | 'result';

const TYPE_LABELS: Record<string, string> = {
  school_bus: 'School Bus',
  shuttle: 'Shuttle',
  service_provider: 'Service Provider',
  other: 'Other',
};

function getTodayWindows(windows: TrustedOperator['allowed_windows']): string {
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const today = dayNames[new Date().getDay()] ?? 'MON';
  const todayWindows = windows.filter((w) => w.days.includes(today));
  if (todayWindows.length === 0) return 'No windows today';
  return todayWindows.map((w) => `${w.from} – ${w.until}`).join(', ');
}

export function TrustedOperatorEntryPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('select');
  const [selected, setSelected] = useState<TrustedOperator | null>(null);
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [result, setResult] = useState<TrustedOperatorEntryResult | null>(null);

  const operatorsQuery = useQuery({
    queryKey: ['guard', 'trusted-operators'],
    queryFn: getGuardTrustedOperators,
  });

  const entryMutation = useMutation({
    mutationFn: (params: { operatorId: string; vehiclePlate?: string | undefined }) =>
      recordTrustedOperatorEntry(params.operatorId, {
        vehiclePlate: params.vehiclePlate ?? undefined,
      }),
    onSuccess: (data) => {
      setResult(data);
      setStep('result');
    },
  });

  const handleSelect = (op: TrustedOperator) => {
    setSelected(op);
    setVehiclePlate('');
    setStep('confirm');
  };

  const handleGrantEntry = () => {
    if (!selected) return;
    entryMutation.mutate({
      operatorId: selected.id,
      vehiclePlate: vehiclePlate.trim() || undefined,
    });
  };

  const handleReset = () => {
    setStep('select');
    setSelected(null);
    setVehiclePlate('');
    setResult(null);
  };

  // ─── Select Step ────────────────────────────────────────────────────────────
  if (step === 'select') {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">
          {t('guard.trustedOperator.title')}
        </h2>

        {operatorsQuery.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {operatorsQuery.data?.length === 0 && (
          <div className="text-center py-8 text-[var(--text-tertiary)]">
            <Bus className="size-10 mx-auto mb-3 opacity-40" />
            <p>{t('guard.trustedOperator.empty')}</p>
            <p className="text-xs mt-1">{t('guard.trustedOperator.emptyHint')}</p>
          </div>
        )}

        <div className="space-y-2">
          {operatorsQuery.data?.map((op) => (
            <button
              key={op.id}
              onClick={() => handleSelect(op)}
              className="w-full text-left rounded-lg border border-border p-4 hover:bg-accent/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[var(--text-primary)]">{op.name}</p>
                  <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                    {getTodayWindows(op.allowed_windows)}
                  </p>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {TYPE_LABELS[op.operator_type] ?? op.operator_type}
                </Badge>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ─── Confirm Step ───────────────────────────────────────────────────────────
  if (step === 'confirm' && selected) {
    return (
      <div className="space-y-5">
        <button
          onClick={handleReset}
          className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          <ArrowLeft className="size-4" />
          {t('common.back')}
        </button>

        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[var(--text-primary)]">{selected.name}</h3>
            <Badge variant="secondary">
              {TYPE_LABELS[selected.operator_type] ?? selected.operator_type}
            </Badge>
          </div>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('guard.trustedOperator.todayWindows')} {getTodayWindows(selected.allowed_windows)}
          </p>
          {selected.notes && (
            <p className="text-sm text-[var(--text-tertiary)] italic">{selected.notes}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="vehicle-plate">{t('guard.trustedOperator.vehiclePlate')}</Label>
          <Input
            id="vehicle-plate"
            value={vehiclePlate}
            onChange={(e) => setVehiclePlate(e.target.value)}
            placeholder="KCA 123A"
            maxLength={20}
          />
        </div>

        <Button
          onClick={handleGrantEntry}
          disabled={entryMutation.isPending}
          className="w-full h-12 text-base"
          size="lg"
        >
          {entryMutation.isPending
            ? t('guard.trustedOperator.processing')
            : t('guard.trustedOperator.grantEntry')}
        </Button>

        {entryMutation.isError && (
          <p className="text-sm text-destructive text-center">{t('guard.trustedOperator.error')}</p>
        )}
      </div>
    );
  }

  // ─── Result Step ────────────────────────────────────────────────────────────
  if (step === 'result' && result) {
    const isGranted = result.status === 'granted';

    return (
      <div className="space-y-5">
        <div
          className={`rounded-lg p-6 text-center ${
            isGranted ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          <div
            className={`size-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
              isGranted ? 'bg-green-100' : 'bg-red-100'
            }`}
          >
            {isGranted ? (
              <Check className="size-6 text-green-600" />
            ) : (
              <X className="size-6 text-red-600" />
            )}
          </div>

          <h3 className={`text-lg font-semibold ${isGranted ? 'text-green-800' : 'text-red-800'}`}>
            {isGranted ? t('guard.trustedOperator.granted') : t('guard.trustedOperator.denied')}
          </h3>

          <p className="text-sm mt-1 text-[var(--text-secondary)]">{selected?.name}</p>

          {isGranted && vehiclePlate && (
            <p className="text-xs mt-1 text-[var(--text-tertiary)]">
              {t('guard.trustedOperator.plate')} {vehiclePlate}
            </p>
          )}

          {!isGranted && result.reason === 'OUTSIDE_ALLOWED_WINDOW' && result.nextWindow && (
            <p className="text-sm mt-2 text-red-700">
              {`Next valid window: ${result.nextWindow.day} ${result.nextWindow.from}u2013${result.nextWindow.until}`}
            </p>
          )}

          {!isGranted && result.reason === 'DAILY_LIMIT_REACHED' && (
            <p className="text-sm mt-2 text-red-700">
              {`Daily limit reached (${result.count ?? 0}/${result.limit ?? 0})`}
            </p>
          )}

          {!isGranted && result.reason === 'OPERATOR_SUSPENDED' && (
            <p className="text-sm mt-2 text-red-700">{t('guard.trustedOperator.suspended')}</p>
          )}
        </div>

        <Button onClick={handleReset} variant="outline" className="w-full">
          {t('guard.trustedOperator.backToQueue')}
        </Button>
      </div>
    );
  }

  return null;
}
